export type AuditableCompassReportingObject = {
    id: number;
    createdAt: string;
    updatedAt: string;
};

export type TimedCompassReportingObject = {
    startDate: string;
    endDate: string;
    elapsedSeconds: number;
} & AuditableCompassReportingObject;

export type PlayAudit = {
    playResultId: number;
    status: PlayAuditStatus;
} & AuditableCompassReportingObject;

export type PlayAuditStatus =
    | 'Pending'
    | 'TriggeredByUiAction'
    | 'TriggeredByScheduler'
    | 'StartingExecution'
    | 'RetrievingDataMartData'
    | 'RetrievingFileData'
    | 'GeneratingPlay'
    | 'GeneratingTalkingPoints'
    | 'RetrievingMergeFieldData'
    | 'GeneratingRecommendations'
    | 'AwaitingSalesforceResponse'
    | 'SyncingLifecycleData'
    | 'CompletingExecution';

// These are the statuses that indicate the play execution is past the point of making the request to Salesforce
export const postSalesforceRequestPlayAuditStatuses: PlayAuditStatus[] = [
    'CompletingExecution',
    'SyncingLifecycleData',
    'AwaitingSalesforceResponse',
];

// These are the statuses that indicate the play execution has begun but no reporting details have been generated
// TODO: revisit once cloud-10796 has been merged to add new play audit status here 'TriggeredByScheduler'
export const executionStartPlayAuditStatuses: PlayAuditStatus[] = ['TriggeredByUiAction', 'StartingExecution'];

export type PlayDatabaseInteraction = {
    playResultId: number;
    databaseType: string;
    databaseSystem: string;
    numRecordsErrored: number;
    status: string;
    query: string;
    rawRequest: string;
    rawResponse: string;
    rawException: string;
} & TimedCompassReportingObject;

export type PlayError = {
    playResultId: number;
    playSalesforceInteractionId: number;
    playDatabaseInteractionId: number;
    error: string;
    errorType?: string;
} & AuditableCompassReportingObject;

export type PlayResult = {
    playId: number;
    divisionId: number;
    executionId: string;
    query: string;
    playConfig: string;
    divisionConfig: string;
    fileName: string;
    fileBytes: number;
    executionType: 'OnDemand' | 'Scheduled';
} & TimedCompassReportingObject;

export type PlayExecutionReportingData = {
    numRecordsSelected: number;
    numRecordsCreated: number;
    numRecordsUpdated: number;
    numRecordsRejected: number;
    numRecordsFailed: number;
    status: PlayResultCompletionStatus; // not the same as the PlayResult field 'status'
} & PlayResult;

export type PlayResultCompletionStatus = 'In Progress' | 'Partial Success' | 'Complete' | 'Failure' | 'Not Available';

export type PlayResultDetail = {
    playResultId: number;
    actionType: PlayResultDetailsActionType;
    numRecordsSelected: number;
    numRecordsSuccessful: number;
    numRecordsRejected: number;
    numRecordsFailed: number;
} & AuditableCompassReportingObject;

export type PlaySalesforceInteraction = {
    playResultId: number;
    statusCode: string;
    numRecordsErrored: number;
    salesforceObject: string;
    rawException: string;
    type: string;
} & TimedCompassReportingObject;

export type ErrorLink = {
    errorType: string;
    linkText: string;
};

export type PlayResultDetailsActionType = 'create' | 'update';
